<template>
    <div class="login-container">
        <div class="login-bg">
            <img src="@/assets/img/login-bg2.jpg" alt="注册背景" class="login-bg-img" />
        </div>

        <div class="login-card-wrapper">
            <div class="login-card">
                <h2 class="login-title">用户注册</h2>

                <form class="login-form" @submit.prevent="handleRegister">
                    <!-- 全局错误提示（添加抖动触发类） -->
                    <div
                            v-if="globalError"
                            class="login-global-error"
                            :class="{ 'login-error-shake': shakeTrigger }"
                            :key="shakeTrigger"
                    >
                        <span>{{ globalError }}</span>
                    </div>

                    <!-- 账号输入框 -->
                    <div class="login-form-item">
                        <label class="login-form-label">昵称</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="text"
                                    v-model="form.userName"
                                    class="login-form-input"
                                    :title="errorText.userName"
                                    maxlength="20"
                                    placeholder="请输入账号"
                                    ref="userNameInput"
                                    @input="handleInput('userName')"
                                    @blur="validateField('userName')"
                                    @focus="showTooltip($event, errorText.userName)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.userName && form.userName.trim()" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.userName }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 手机号输入框 -->
                    <div class="login-form-item">
                        <label class="login-form-label">手机号</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="tel"
                                    v-model="form.phoneNumber"
                                    class="login-form-input"
                                    :title="errorText.phoneNumber"
                                    maxlength="11"
                                    placeholder="请输入11位手机号"
                                    ref="phoneNumberInput"
                                    @input="handleInput('phoneNumber')"
                                    @blur="validateField('phoneNumber')"
                                    @focus="showTooltip($event, errorText.phoneNumber)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.phoneNumber && form.phoneNumber.trim()" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.phoneNumber }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 邮箱输入框 -->
                    <div class="login-form-item">
                        <label class="login-form-label">邮箱</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="text"
                                    v-model="form.email"
                                    placeholder="请输入邮箱"
                                    class="login-form-input"
                                    maxlength="100"
                                    ref="emailInput"
                                    @input="handleInput('email')"
                                    @blur="validateField('email')"
                                    @focus="showTooltip($event, errorText.email)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.email" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.email }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 验证码区域（改为发邮箱） -->
                    <div class="login-form-item">
                        <label class="login-form-label">验证码</label>
                        <div class="login-code-container">
                            <div class="login-input-wrapper login-code-input-wrapper">
                                <input
                                        type="text"
                                        v-model="form.code"
                                        class="login-form-input login-code-input"
                                        :title="errorText.code"
                                        maxlength="6"
                                        placeholder="请输入6位验证码"
                                        ref="codeInput"
                                        @input="handleInput('code')"
                                        @blur="validateField('code')"
                                        @focus="showTooltip($event, errorText.code)"
                                />
                                <transition name="login-tooltip-fade">
                                    <div v-if="fieldError.code && form.code.trim()" class="login-error-tooltip login-code-tooltip">
                                        <span class="login-tooltip-text">{{ errorText.code }}</span>
                                        <div class="login-tooltip-arrow"></div>
                                    </div>
                                </transition>
                            </div>
                            <button
                                    type="button"
                                    class="login-get-code-btn"
                                    :disabled="isCodeDisabled || !isEmailValid"
                                    @click="getVerificationCode"
                            >
                                {{ codeText }}
                            </button>
                        </div>
                    </div>

                    <!-- 密码输入框 -->
                    <div class="login-form-item">
                        <label class="login-form-label">密码</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="password"
                                    v-model="form.userPwd"
                                    class="login-form-input"
                                    :title="errorText.userPwd"
                                    maxlength="20"
                                    placeholder="请输入密码（6-20位）"
                                    ref="userPwdInput"
                                    @input="handleInput('userPwd')"
                                    @blur="validateField('userPwd')"
                                    @focus="showTooltip($event, errorText.userPwd)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.userPwd && form.userPwd.trim()" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.userPwd }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 确认密码输入框 -->
                    <div class="login-form-item">
                        <label class="login-form-label">确认密码</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="password"
                                    v-model="form.confirmPwd"
                                    class="login-form-input"
                                    :title="errorText.confirmPwd"
                                    maxlength="20"
                                    placeholder="请确认密码"
                                    ref="confirmPwdInput"
                                    @input="handleInput('confirmPwd')"
                                    @blur="validateField('confirmPwd')"
                                    @focus="showTooltip($event, errorText.confirmPwd)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.confirmPwd && form.confirmPwd.trim()" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.confirmPwd }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 注册按钮 -->
                    <div class="login-form-actions">
                        <button
                                type="submit"
                                class="login-submit-btn"
                                :disabled="isLoading || !isAllValid"
                        >
                            <span v-if="!isLoading">注册</span>
                            <span v-if="isLoading" class="login-loading-spinner">⟳</span>
                        </button>
                    </div>

                    <!-- 登录链接 -->
                    <div class="login-links">
                        <button type="button" class="login-link-btn" @click="goToLogin">
                            已有账号？立即登录
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { useRegisterLogic } from './register.js'

    const {
        form,
        fieldError,
        errorText,
        globalError,
        shakeTrigger,  // 引入动画触发器
        isLoading,
        codeText,
        isCodeDisabled,
        isEmailValid,
        isAllValid,
        userNameInput,
        emailInput,
        userPwdInput,
        confirmPwdInput,
        phoneNumberInput,
        codeInput,
        handleInput,
        validateField,
        getVerificationCode,
        handleRegister,
        goToLogin
    } = useRegisterLogic()

    // 自定义气泡提示显示逻辑
    const showTooltip = (e, text) => {
        if (!text) return
        e.target.setAttribute('title', text)
    }
</script>

<style scoped>
    @import '../../assets/css/login.css';
</style>